import autoBind from 'auto-bind';
import uuid from '../../utils/uuid/index.js';
import translator from '../../utils/translator/index.js';

export class EMultiselectState {
  constructor(component) {
    autoBind(this);
    this.requestRender = component.requestRender;
    this.actionlistRender = component.actionlistRender.bind(component);
    this.footerRender = component.footerRender.bind(component);

    this._options = [];
    this._popupOpened = false;
    this._disabled = false;
    this._readonly = false;
    this._loading = false;
    this._invalid = false;
    this._placeholder = '';
    this._alwaysShowSearch = false;
    this._isSearching = false;
    this._labelMergeLimit = Infinity;
    this._translations = {};
    this._isEditable = true;
    this._selectionMax = null;
  }

  get options() {
    return this._options;
  }

  set options(value) {
    this._options = value.map(this._extendOptionWithDefaults);
  }

  get selectedOptions() {
    return this._options.filter(option => option.selected);
  }

  set selectedOptions(selectedOptions) {
    this._options.forEach((option) => {
      option.selected = selectedOptions.includes(option.uuid);
    });

    this.requestRender();
    this.footerRender();
  }

  selectOptionsByValue(selectedOptionValues) {
    this._options.forEach((option) => {
      option.selected = selectedOptionValues.includes(option.value);
    });

    this.requestRender();
    this.actionlistRender();
    this.footerRender();
  }

  get optionsForActionlist() {
    return this._options.map(option => ({
      type: 'option',
      content: option.content,
      value: option.uuid,
      selected: option.selected,
      disabled: option.disabled || !this.isEditable
    }));
  }

  get isAllOptionsSelected() {
    return this._options.filter(option => !option.disabled).every(option => option.selected);
  }

  get popupOpened() {
    return this._popupOpened;
  }

  set popupOpened(value) {
    this._popupOpened = value;
  }

  get disabled() {
    return this._disabled;
  }

  set disabled(value) {
    this._disabled = value;
  }

  get readonly() {
    return this._readonly;
  }

  set readonly(value) {
    this._readonly = value;
  }

  get loading() {
    return this._loading;
  }

  set loading(value) {
    this._loading = value;
  }

  get invalid() {
    return this._invalid;
  }

  set invalid(value) {
    this._invalid = value;
  }

  get placeholder() {
    return this._placeholder;
  }

  set placeholder(value) {
    this._placeholder = value;
  }

  get alwaysShowSearch() {
    return this._alwaysShowSearch;
  }

  set alwaysShowSearch(value) {
    this._alwaysShowSearch = value;
  }

  get labelMergeLimit() {
    return this._labelMergeLimit;
  }

  set labelMergeLimit(value) {
    this._labelMergeLimit = value;
  }

  get isSearching() {
    return this._isSearching;
  }

  set isSearching(value) {
    this._isSearching = value;

    this.footerRender();
  }

  get translations() {
    return this._translations;
  }

  set translations(value) {
    this._translations = value;
  }

  get isEditable() {
    this._isEditable = !this._disabled && !this._readonly && !this._loading;

    return this._isEditable;
  }

  get selectionMax() {
    return this._selectionMax;
  }

  set selectionMax(value) {
    this._selectionMax = value;
  }

  get hasSelectionMaxError() {
    return this.selectedOptions.length > this.selectionMax;
  }

  clearOption(uuid) {
    const foundOption = this._getOptionByUuid(uuid);
    foundOption.selected = false;

    this.requestRender();
    this.actionlistRender();
    this.footerRender();
  }

  selectAllOptions() {
    this._options.forEach(option => {
      if (option.disabled) { return; }

      option.selected = true;
    });

    this.requestRender();
    this.actionlistRender();
    this.footerRender();
  }

  clearAllOptions() {
    this._options.forEach(option => {
      if (option.disabled) { return; }

      option.selected = false;
    });

    this.requestRender();
    this.actionlistRender();
    this.footerRender();
  }

  upsertOption(optionData) {
    const foundOption = this._getOptionByUuid(optionData.uuid);
    const index = this._options.indexOf(foundOption);

    if (index > -1) {
      this._options = this._options.slice();
      this._options[index] = optionData;
    } else {
      this._options = this._options.concat(optionData);
    }

    this.requestRender();
    this.actionlistRender();
    this.footerRender();
  }

  deleteOption({ detail }) {
    this._options = this._options.filter(option => option.uuid !== detail.uuid);

    this.requestRender();
    this.actionlistRender();
    this.footerRender();
  }

  setTranslations() {
    this._translations = {
      emptyState: translator.translate('components.multiselect.popup.emptyState')
    };

    this.requestRender();
    this.footerRender();
  }

  _getOptionByUuid(uuid) {
    return this._options.find(option => option.uuid === uuid);
  }

  _extendOptionWithDefaults(option) {
    return Object.assign({
      disabled: false,
      selected: false,
      type: 'option',
      uuid: uuid()
    }, option);
  }
}

export default EMultiselectState;
